import React, { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { FalhaRelatorioItem, fetchFalhaDetalhe, renovarTokenHinova } from '../../../api/bancoSync/bancoSyncApi';
import { notify } from '../../../shared/feedback/notify';
import ModalSync from '../../../components/sync-painel/ModalSync';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { formatarDataHoraCurta } from '../../../components/sync-painel/statusBadge';
import '../../../shared/feedback/aviso-de-tela.css';

const ROTULO_ORIGEM: Record<string, string> = {
  associados: 'Associados (SGA)',
  veiculos: 'Veículos (SGA)',
  'entradas-financeiras': 'Boletos (SGA)',
  eventos: 'Eventos (SGA)',
  nasajon: 'Financeiro Nasajon',
};

const MOTIVO_AUSENTE = 'não registrado (relatório anterior a 22/08/2026)';

/** Linha normalizada do detalhe — as 4 origens viram a mesma tabela [o quê, onde, motivo]. */
type ItemDetalhe = { id: string; oque: string; onde: string; motivo: string };

const normalizarPayload = (origem: string, payload: unknown): ItemDetalhe[] => {
  const p = (payload ?? {}) as Record<string, Array<Record<string, unknown>>>;
  const arr = (k: string) => (Array.isArray(p[k]) ? p[k] : []);
  const itens: ItemDetalhe[] = [];

  if (origem === 'nasajon') {
    arr('falhas').forEach((f, i) => {
      itens.push({
        id: `falha-${i}`,
        oque: f.failedPage !== undefined && f.failedPage !== null ? `Etapa parou na página ${f.failedPage}` : 'Etapa falhou',
        onde: String(f.step ?? '?'),
        motivo: String(f.detail ?? MOTIVO_AUSENTE),
      });
    });
    arr('avisos').forEach((a, i) => {
      const paginas = Array.isArray(a.fatalPages) ? a.fatalPages.join(', ') : '?';
      itens.push({
        id: `aviso-${i}`,
        oque: `Página(s) pulada(s): ${paginas}`,
        onde: String(a.step ?? '?'),
        motivo: String(a.detail ?? MOTIVO_AUSENTE),
      });
    });
    return itens;
  }

  if (origem === 'entradas-financeiras') {
    arr('janelasPuladas').forEach((j, i) => {
      itens.push({
        id: `janela-${i}`,
        oque: `Janela de datas ${j.dataInicial} a ${j.dataFinal}`,
        onde: `Base ${j.base}`,
        motivo: String(j.erro ?? MOTIVO_AUSENTE),
      });
    });
    arr('paginasPuladas').forEach((pg, i) => {
      itens.push({
        id: `pagina-${i}`,
        oque: `Página ${pg.pagina} de ${pg.dataInicial} a ${pg.dataFinal}`,
        onde: `Base ${pg.base}`,
        motivo: String(pg.erro ?? MOTIVO_AUSENTE),
      });
    });
    return itens;
  }

  // associados / veiculos
  arr('janelasPuladas').forEach((j, i) => {
    const offset = Number(j.offset ?? 0);
    const tamanho = Number(j.tamanho ?? 0);
    itens.push({
      id: `janela-${i}`,
      oque: `Registros ${offset} a ${offset + tamanho}`,
      onde: `Base ${j.base} — situação ${j.situacao}`,
      motivo: String(j.erro ?? MOTIVO_AUSENTE),
    });
  });
  arr('erros').forEach((e, i) => {
    itens.push({
      id: `erro-${i}`,
      oque: 'Situação inteira',
      onde: `Base ${e.base} — situação ${e.situacao}`,
      motivo: String(e.erro ?? MOTIVO_AUSENTE),
    });
  });
  return itens;
};

const PARECE_TOKEN = /401|403|token|autentica/i;

interface PendenciaDetalheModalProps {
  item: FalhaRelatorioItem;
  onClose: () => void;
  onReprocessar: (item: FalhaRelatorioItem) => void;
  reprocessando: boolean;
}

/**
 * Detalhe de uma pendência: cada janela/página/etapa que ficou de fora, COM O MOTIVO —
 * é o que separa "instabilidade passageira, reprocesse" de "credencial quebrada,
 * reprocessar é perda de tempo" (lição do incidente da RJ de 21/08/2026, diagnosticado
 * à mão porque a tela só mostrava a contagem). Quando o motivo dominante cheira a
 * autenticação, o modal oferece a renovação do token Hinova antes do reprocessamento.
 */
const PendenciaDetalheModal: React.FC<PendenciaDetalheModalProps> = ({ item, onClose, onReprocessar, reprocessando }) => {
  const { data, error, isError, isLoading } = useQuery({
    queryKey: ['banco-sync', 'falha', item.id],
    queryFn: () => fetchFalhaDetalhe(item.id),
  });

  useEffect(() => {
    if (isError) {
      notify.errorFrom(error, 'Não foi possível carregar o detalhe da pendência. Feche o modal e tente de novo.', {
        toastId: 'banco-sync-falha-detalhe',
      });
    }
  }, [isError, error]);

  const tokenMutation = useMutation({
    mutationFn: renovarTokenHinova,
    onSuccess: () => notify.success('Tokens Hinova (RJ e BH) renovados. Agora o reprocessamento tende a passar.'),
    onError: (e) =>
      notify.errorFrom(e, 'A renovação do token falhou — confira as credenciais Hinova no servidor antes de reprocessar.'),
  });

  const itens = useMemo(() => (data ? normalizarPayload(data.origem, data.payload) : []), [data]);
  const pareceToken = useMemo(() => itens.some((i) => PARECE_TOKEN.test(i.motivo)), [itens]);

  const colunas = useMemo<ColunaTabela<ItemDetalhe>[]>(
    () => [
      { titulo: 'O que ficou de fora', largura: 200, texto: (i) => i.oque },
      { titulo: 'Onde', largura: 170, texto: (i) => i.onde },
      { titulo: 'Motivo', largura: 320, texto: (i) => i.motivo },
    ],
    [],
  );

  return (
    <ModalSync
      titulo={`Pendência — ${ROTULO_ORIGEM[item.origem] ?? item.origem}`}
      subtitulo={`Gerada em ${formatarDataHoraCurta(item.criadoEm)} · ${item.arquivo}`}
      onClose={onClose}
      largura="min(920px, 94vw)"
      footer={
        <>
          <button type="button" className="filtro-btn-secondary" onClick={onClose}>
            Fechar
          </button>
          <button
            type="button"
            className="filtro-btn-primary"
            disabled={reprocessando}
            onClick={() => onReprocessar(item)}
          >
            {reprocessando ? 'Reprocessando...' : 'Reprocessar somente isto'}
          </button>
        </>
      }
    >
      {isLoading && <div className="admin-vazio">Carregando o detalhe da pendência...</div>}

      {isError && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar o detalhe desta pendência.
        </div>
      )}

      {data && (
        <>
          {pareceToken && (
            <div className="aviso-de-tela" style={{ marginBottom: 14 }}>
              O motivo dominante aponta para <strong>autenticação na Hinova</strong> (a causa mais comum de falha).
              Renove o token antes de reprocessar — sem isso, o reprocessamento tende a colher as mesmas falhas.
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  disabled={tokenMutation.isPending}
                  onClick={() => tokenMutation.mutate()}
                >
                  {tokenMutation.isPending ? 'Renovando token...' : 'Renovar token Hinova'}
                </button>
              </div>
            </div>
          )}

          <TabelaSistema
            colunas={colunas}
            itens={itens}
            chave={(i) => i.id}
            vazio="O relatório não detalha itens — reprocesse para tentar de novo."
          />

          <p className="admin-hint">
            O reprocessamento refaz somente os itens acima. O que passar sai da lista; o que persistir vira uma nova
            pendência, com o motivo atualizado.
          </p>
        </>
      )}
    </ModalSync>
  );
};

export default PendenciaDetalheModal;
